import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/apiClient";

// App Store and Google Play icons
const AppStoreIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GooglePlayIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
  </svg>
);

const ReviewModal = ({ open, onOpenChange, streakDays, onReviewSubmitted, onRemindLater, onDismiss }) => {
  // TODO: Replace these with actual App Store and Google Play URLs when the app is published
  const APP_STORE_URL = "https://apps.apple.com/app/proud-every-day/id000000000";
  const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.proudeveryday.app";

  const handleStoreClick = async (store) => {
    // Track that user clicked to review
    try {
      await apiFetch('/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: 5, feedback: `Clicked to review on ${store}` })
      });
    } catch (error) {
      console.error('Error tracking review click:', error);
    }

    // Open the store
    const url = store === 'apple' ? APP_STORE_URL : GOOGLE_PLAY_URL;
    window.open(url, '_blank');
    
    toast.success("Thanks for supporting Proud Every Day! 🎉");
    onReviewSubmitted();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            🎉 {streakDays} Day Streak!
          </DialogTitle>
          <DialogDescription className="text-center">
            You're on fire! We'd love your support.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Streak celebration */}
          <div className="text-center mb-4">
            <p className="text-sm text-muted-foreground">
              You've been proud for <span className="font-bold text-emerald-600">{streakDays} days</span> in a row!
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              If you're enjoying the app, would you mind leaving us a review? It really helps!
            </p>
          </div>

          {/* Star decoration */}
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className="w-6 h-6 fill-amber-400 text-amber-400"
              />
            ))}
          </div>

          {/* Store buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={() => handleStoreClick('apple')}
              className="bg-black hover:bg-gray-800 text-white flex items-center justify-center gap-2"
              data-testid="rate-app-store-btn"
            >
              <AppStoreIcon />
              Rate on App Store
            </Button>
            <Button
              onClick={() => handleStoreClick('google')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2"
              data-testid="rate-google-play-btn"
            >
              <GooglePlayIcon />
              Rate on Google Play
            </Button>
          </div>

          {/* Secondary actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onRemindLater}
              className="flex-1"
              data-testid="remind-later-btn"
            >
              Remind Me Later
            </Button>
            <Button
              variant="ghost"
              onClick={onDismiss}
              className="flex-1 text-muted-foreground"
              data-testid="no-thanks-btn"
            >
              No Thanks
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
